<template>
	<j-card class="bg5">
		<j-expendable-box :expand="isExpanded">
			<div slot="header" class="p2 pl3 pr3 fs3 c2 fw7">
				<slot name="header">{{ entity.name }}</slot>
			</div>
			<div slot="body" class="p2">
				<!-- Testing -->
				<div
					v-if="pinnable"
					class="flex xs-right xs-12 cu2 c2 fs4 fw7"
					@click="isModal = !isModal"
				>
					<j-button type="tertiary">
						<span v-if="!isModal">{{ $t("pin") }}</span>
						<span v-else>{{ $t("unpin") }}</span>
						<crm-icon name="expand" size="small" color="c2" />
					</j-button>
				</div>

				<!-- Credentials -->
				<entity-credentials
					:id="entity.id"
					:mode="mode"
					:typename="entity.__typename"
					:payload="computedEntity.credentials"
					:hasActions="hasActions"
					@updated="$emit('updated')"
				/>

				<!-- Contacts -->
				<entity-contacts
					v-if="
						entity.__typename !== 'object' &&
							entity.__typename !== 'organization'
					"
					:id="entity.id"
					:mode="mode"
					:contacts="computedEntity.contacts"
					:hasActions="hasActions"
					@added="$emit('added')"
					@updated="$emit('updated')"
					@deleted="$emit('deleted')"
				/>

				<!-- Identities -->
				<entity-identities
					v-if="
						entity.__typename === 'individual' &&
							entity.__typename !== 'organization'
					"
					:id="entity.id"
					:mode="mode"
					:identities="computedEntity.identities"
					:hasActions="hasActions"
					@added="$emit('added')"
					@updated="$emit('updated')"
					@deleted="$emit('deleted')"
				/>

				<!-- Addresses -->
				<entity-addresses
					v-if="
						entity.__typename !== 'internal' &&
							entity.__typename !== 'organization'
					"
					:id="entity.id"
					:mode="mode"
					:addresses="computedEntity.addresses"
					:hasActions="hasActions"
					@added="$emit('added')"
					@updated="$emit('updated')"
					@deleted="$emit('deleted')"
				/>

				<!-- Facts -->
				<entity-facts
					v-if="
						entity.__typename !== 'internal' &&
							entity.__typename !== 'organization'
					"
					:id="entity.id"
					:mode="mode"
					:facts="computedEntity.facts"
					:hasActions="hasActions"
					@added="$emit('added')"
					@updated="$emit('updated')"
					@deleted="$emit('deleted')"
				/>

				<!-- Relationships -->
				<entity-relationships
					v-if="
						entity.__typename !== 'object' && entity.__typename !== 'internal'
					"
					:mode="mode"
					:relationships="computedEntity.relationships"
					:sourceId="entity.id"
					:hasActions="hasActions"
					@deleted="$emit('deleted')"
					@added="$emit('added')"
				/>

				<!-- Footer slot -->
				<slot name="footer"></slot>
			</div>
		</j-expendable-box>

		<!-- -------------- -->
		<!-- Special modal view that will be displayed fixed -->
		<!-- -------------- -->
		<div class="entity-card-modal" v-if="isModal">
			<!-- Header -->
			<div class="flex xs-12 xs-middle wrap p4 bg1 entity-card-modal-sticky">
				<div class="xs-6 fs5 fw7 lh125 c5">{{ entity.name }}</div>
				<div class="flex xs-right xs-6 fs5 fw7 lh125">
					<crm-icon
						size="large"
						color="c5"
						name="cross"
						class="cu2"
						@click.native="isModal = false"
					/>
				</div>
			</div>
			<!-- Body -->
			<core-entity-card :entity="entity" :isExpanded="true" :pinnable="false" />
		</div>
	</j-card>
</template>

<script>
/**
 * This component is responsible for showing all things around entity (individual, object...)
 * It shows different sections:
 * Credentials
 * Identities
 * Contacts
 * Addresses
 * Facts
 * It follows GraphQL schema for data
 */
// TODO: handle different types of entities - objects and also companies
import JExpendableBox from "@/components/JExpendableBox/"
import JCard from "@/components/JCard/"
import JList from "@/components/JList/"
import JButton from "@/components/JButton/"
import CrmIcon from "@/components/CrmIcon/"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreEntityCard from "@/modules/core/components/CoreEntityCard/index.vue"
import EntityCredentials from "./EntityCredentials"
import EntityContacts from "./EntityContacts"
import EntityIdentities from "./EntityIdentities"
import EntityAddresses from "./EntityAddresses"
import EntityFacts from "./EntityFacts"
import EntityRelationships from "./EntityRelationships"
import dayjs from "dayjs"

export default {
	name: "core-entity-card",

	components: {
		JExpendableBox,
		CoreEntityCard,
		EntityCredentials,
		EntityContacts,
		EntityIdentities,
		EntityAddresses,
		EntityFacts,
		EntityRelationships,
		JCard,
		JList,
		JButton,
		CoreModal,
		CrmIcon
	},

	props: {
		/**
		 * It's an object we get from GraphQL
		 * It could be individual or holder or company or object (e.g. car, house)
		 * We assume this object includes identities, contacts, addresses, facts
		 */
		entity: {
			type: Object,
			required: true,
			default: () => ({})
		},

		/**
		 * Whether to expand the card
		 */
		isExpanded: {
			type: Boolean,
			default: true
		},

		/**
		 * Whether to show pin option
		 * Pinability is a ability to pin the card to the right top corner of screen
		 */
		pinnable: {
			type: Boolean,
			default: true
		},

		/**
		 * Whether to show actions
		 * Actions such as update, add, delete
		 */
		hasActions: {
			type: Boolean,
			default: false
		},

		/**
		 * Edit mode, 'modal' or 'inline'
		 * Default is modal
		 */
		mode: {
			type: String,
			required: false,
			default: "modal"
		}
	},

	data() {
		return {
			isModal: false
		}
	},

	computed: {
		/**
		 * Will return computed list with contacts, addresses, identities, facts ready to be displayed
		 */
		computedEntity() {
			/**
			 * Helper function to massage credentials
			 * Based on typename return individual or company credentials
			 */
			const massageCredentials = credentials => {
				const {
					name,
					gender,
					salutation,
					englishFirstName,
					englishLastName,
					chineseFirstName,
					chineseLastName,
					dateOfBirth,
					maritalStatus,
					occupation,
					countryOfResidency,
					acceptsMarketing,
					preferredCommunicationChannel,
					preferredLanguage,
					natureOfBusiness,
					registrationNumber,
					internalCode,
					isActive,
					source
				} = credentials

				// Individual
				if (credentials.__typename === "individual")
					return {
						name,
						gender,
						salutation,
						englishFirstName,
						englishLastName,
						chineseFirstName,
						chineseLastName,
						dateOfBirth,
						maritalStatus,
						occupation,
						countryOfResidency,
						acceptsMarketing,
						preferredCommunicationChannel,
						preferredLanguage
					}

				// Company
				if (credentials.__typename === "company")
					return {
						name,
						natureOfBusiness,
						registrationNumber
					}

				// Object
				if (credentials.__typename === "object") return { name }

				// Internal
				if (credentials.__typename === "internal")
					return {
						englishFirstName,
						englishLastName,
						chineseFirstName,
						chineseLastName,
						internalCode,
						isActive
					}

				// organization
				if (credentials.__typename === "organization")
					return {
						name,
						internalCode,
						isActive,
						source
					}
			}

			/**
			 * Helper function to massage addresses into proper format
			 */
			const massageAddresses = addresses => {
				if (!addresses) return []

				return addresses.map(item => {
					if (!item.fields) return
					let fields

					try {
						fields = JSON.parse(item.fields)
					} catch (e) {
						console.error("Error in parsing JSON", e)
					}

					return {
						...item,
						fields: fields
							? Object.keys(fields).map(key => ({
									type: this.$t(key),
									value: fields[key]
							  }))
							: []
					}
				})
			}

			/**
			 * Helper function to massage contacts, identities and more
			 */
			const massageList = list => {
				if (!list) return []

				return list.map(item => ({
					id: item.id,
					type: item.type,
					value: item.value
				}))
			}

			/**
			 * Helper function to massage facts
			 * TODO: translate boolean, format date etc?
			 */
			const massageFacts = facts => {
				if (!facts) return []

				return facts.map(item => ({
					id: item.id,
					label: item.type,
					value: item.value
				}))
			}

			/**
			 * Helper function to massage relationships
			 */
			const massageRelationships = relationships => {
				if (!relationships) return []
				return relationships
					.filter(item => item?.entity?.id)
					.filter(item => item?.type?.length)
					.map(item => {
						return {
							type: item.type,
							entityName: item.entity ? item.entity.name : "---",
							id: item?.entity?.id,
							facts: item?.entity?.facts
						}
					})
			}

			return {
				credentials: massageCredentials(this.entity),
				addresses: massageAddresses(this.entity.addresses),
				contacts: massageList(this.entity.contacts),
				identities: massageList(this.entity.identities),
				facts: massageFacts(this.entity.facts),
				relationships: massageRelationships(this.entity.relationships)
			}
		}
	}
}
</script>

<style scoped>
@import "../../../../assets/css/tokens.css";

.entity-card-modal {
	position: fixed;
	top: 16px;
	right: 16px;
	width: 85%;
	max-width: 600px;
	height: calc(100vh - 32px);
	overflow: auto;
	z-index: 1000;
	box-shadow: 0 16px 28px -2px rgba(0, 0, 0, 0.25);
	border-radius: var(--borderRadius2);
	background-color: var(--color4);
	animation: slidein-right 220ms ease-in-out;
}
.entity-card-modal-sticky {
	position: sticky;
	top: 0;
	left: 0;
}

@media (--lg) {
	.entity-card-modal {
		width: 33%;
	}
}
</style>
